import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, FlaskConical, Search, X } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { BiomarkerGroup } from '../components/BiomarkerGroup';
import { PageHeader } from '../components/PageHeader';
import { StatusMarker } from '../components/StatusMarker';
import { usePlatform } from '../contexts/PlatformContext';
import { biomarkerCategories, labReport } from '../data/biomarkers';
import { getStatus } from '../utils/biomarkers';
import { gradeFor, gradeScale, panelScore } from '../utils/grade';
import { GradeBadge } from '../components/GradeBadge';
type StatusFilter = 'all' | 'attention' | 'optimal';
const statusFilters: {
  id: StatusFilter;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'attention',
  label: 'Needs attention'
}, {
  id: 'optimal',
  label: 'In range'
}];
export function Results() {
  const {
    goal,
    stage,
    panel
  } = usePlatform();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [category, setCategory] = useState('All');
  const flagged = useMemo(() => panel.filter(b => getStatus(b) !== 'optimal'), [panel]);
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = panel.filter(b => {
      const s = getStatus(b);
      if (category !== 'All' && b.category !== category) return false;
      if (statusFilter === 'attention' && s === 'optimal') return false;
      if (statusFilter === 'optimal' && s !== 'optimal') return false;
      return !q || b.name.toLowerCase().includes(q);
    });
    return biomarkerCategories.map(c => ({
      name: c,
      total: panel.filter(b => b.category === c).length,
      items: filtered.filter(b => b.category === c).sort((a, b) => Number(getStatus(a) === 'optimal') - Number(getStatus(b) === 'optimal'))
    })).filter(g => g.items.length > 0);
  }, [panel, query, statusFilter, category]);
  if (!goal) return <Navigate to="/profile" replace data-id="element-390" data-mp="98faz07swqp8" />;
  const inRangePct = Math.round((panel.length - flagged.length) / panel.length * 100);
  const score = panelScore(panel);
  const grade = gradeFor(score);
  const focusAreas = biomarkerCategories.map(c => ({
    name: c,
    count: flagged.filter(b => b.category === c).length
  })).filter(f => f.count > 0);
  const hasFilters = query || statusFilter !== 'all' || category !== 'All';
  return <div data-id="element-391" data-mp="3hmxmxd0v4qe4">
      <PageHeader eyebrow="Blood results" title={`${panel.length} biomarkers, one clear picture`} description={goal === 'pregnancy' ? 'Read against pregnancy reference ranges, so we focus on what matters for you and your baby.' : 'Heart, metabolism, hormones and recovery — everything behind your wellbeing pack.'} actions={<Button size="lg" className="bg-[#9b8cf0] text-[#0f1017] hover:bg-[#b0a4f5]" onClick={() => navigate('/plan')} data-id="element-393" data-mp="1ie61jyuws6eh">
            See my recommendation <ArrowRight className="size-4" data-id="element-394" data-mp="1s4qylhuxp2va" />
          </Button>} data-id="element-392" data-mp="3hmnvdl066b6b" />

      <div className="mt-10 grid gap-4 lg:grid-cols-[280px_1fr]" data-id="element-395" data-mp="1s6tyqupswcdq">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-396" data-mp="1x2irof7bev12">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-id="element-397" data-mp="zgv7cso4w35w">Your biomarker grade</p>
          <div className="mt-4 flex items-center gap-5" data-id="element-398" data-mp="14fy29kaegt05">
            <GradeBadge grade={grade} size="lg" data-id="element-399" data-mp="19ej05d26j0lf" />
            <div data-id="element-400" data-mp="19coh64agxijz">
              <p className="font-medium" data-id="element-401" data-mp="19gcc97vkad0t">{grade.label}</p>
              <p className="text-sm text-muted-foreground" data-id="element-402" data-mp="1eedr1glyfq6h">Score {score} / 100</p>
              <p className="mt-1 text-xs text-muted-foreground" data-id="element-403" data-mp="2ktxyola1e0in">
                {inRangePct}% in range · <span className="text-[#f5b454]" data-id="element-404" data-mp="2ps9av2nt0n41">{flagged.length} to review</span>
              </p>
            </div>
          </div>
          <ol className="mt-5 flex gap-1" aria-label="Grade scale" data-id="element-405" data-mp="2nem6aann77h5">
            {[...gradeScale].reverse().map(g => <li key={g.grade} className={`flex-1 rounded-md py-1 text-center font-mono text-[10px] ${g.grade === grade.grade ? 'font-semibold' : 'text-muted-foreground'}`} style={g.grade === grade.grade ? {
            backgroundColor: `${g.color}26`,
            color: g.color
          } : {
            backgroundColor: 'rgba(255,255,255,0.04)'
          }} data-id="element-406" data-mp="2je27l2nf7vsw">
                {g.grade}
              </li>)}
          </ol>
          <div className="mt-4" data-id="element-407" data-mp="3g16z7fjrnrmt">
            <StatusMarker goal={goal} stage={stage} size="sm" data-id="element-408" data-mp="3g4cn4rgnsngy" />
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-409" data-mp="3b5i3jnavauvs">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-410" data-mp="3q17mfckrarwa">
            <p className="font-medium" data-id="element-411" data-mp="3l2nzp9yc9c2v">Focus areas</p>
            <p className="flex items-center gap-2 text-xs text-muted-foreground" data-id="element-412" data-mp="8i0lx7utvvym">
              <FlaskConical className="size-3.5 text-[#9b8cf0]" data-id="element-413" data-mp="8fwckae5u4qy" />
              {labReport.lab} · {labReport.date}
            </p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-id="element-414" data-mp="3uq06iaf1fnyr">
            {focusAreas.map(f => <button key={f.name} onClick={() => {
            setCategory(f.name);
            setStatusFilter('attention');
          }} className="flex items-center gap-2 rounded-full border border-[#f5b454]/25 bg-[#f5b454]/[0.06] px-3 py-1.5 text-sm transition-colors hover:border-[#f5b454]/50" data-id="element-415" data-mp="3ie6qsvfw5n6">
                <GradeBadge grade={gradeFor(panelScore(panel.filter(b => b.category === f.name)))} data-id="element-416" data-mp="3gxkcfz44709z" />
                {f.name}
                <span className="font-mono text-[11px] text-[#f5b454]" data-id="element-417" data-mp="321ku7vnajjr4">{f.count}</span>
              </button>)}
          </div>
        </div>
      </div>

      <div className="sticky top-[65px] z-10 -mx-6 mt-8 border-b border-white/10 bg-[#0f1017]/90 px-6 py-4 backdrop-blur" data-id="element-418" data-mp="2nv13ofuyv233">
        <div className="flex flex-col gap-3 md:flex-row md:items-center" data-id="element-419" data-mp="2j416d9hxqi02">
          <div className="relative md:w-72" data-id="element-420" data-mp="2e57tnjah2v0y">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" data-id="element-421" data-mp="2e5zq0wbw8wsa" />
            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search biomarkers" aria-label="Search biomarkers" className="h-10 bg-white/5 pl-9" data-id="element-422" data-mp="297y2nz7fqbb0" />
          </div>
          <div className="flex gap-1 rounded-full border border-white/10 p-1" role="group" aria-label="Filter by status" data-id="element-423" data-mp="2wueepcyqnypx">
            {statusFilters.map(f => <button key={f.id} aria-pressed={statusFilter === f.id} onClick={() => setStatusFilter(f.id)} className={`rounded-full px-3 py-1 text-sm transition-colors ${statusFilter === f.id ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-424" data-mp="1lgaomhv9uuq5">
                {f.label}
              </button>)}
          </div>
          <label className="sr-only" htmlFor="category" data-id="element-425" data-mp="5vi5tr9qabku">Category</label>
          <select id="category" value={category} onChange={e => setCategory(e.target.value)} className="h-10 rounded-md border border-white/10 bg-white/5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#9b8cf0]" data-id="element-426" data-mp="apw1zvw7smqk">
            <option value="All" className="bg-[#0f1017]" data-id="element-427" data-mp="op45h0zeuh0k">All categories</option>
            {biomarkerCategories.map(c => <option key={c} value={c} className="bg-[#0f1017]" data-id="element-428" data-mp="tme1izhb1qxd">{c}</option>)}
          </select>
          {hasFilters && <Button variant="ghost" size="sm" onClick={() => {
          setQuery('');
          setStatusFilter('all');
          setCategory('All');
        }} data-id="element-429" data-mp="2veab43jwwqpi">
              <X className="size-3.5" data-id="element-430" data-mp="w6tuarf89ii1" /> Clear
            </Button>}
        </div>
      </div>

      <div className="mt-6 space-y-4" data-id="element-431" data-mp="1avyg0il8e4uw">
        {groups.length === 0 ? <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center" data-id="element-432" data-mp="15x51qny4e9to">
            <p className="font-medium" data-id="element-433" data-mp="i70zajwo4g49">No biomarkers match</p>
            <p className="mt-1 text-sm text-muted-foreground" data-id="element-434" data-mp="i9nf16tpq1xk">Try a different search or clear your filters.</p>
          </div> : groups.map(g => <BiomarkerGroup key={g.name} category={g.name} items={g.items} totalInCategory={g.total} grade={gradeFor(panelScore(panel.filter(b => b.category === g.name)))} data-id="element-435" data-mp="s7l0xno3nmn0" />)}
      </div>
    </div>;
}